/**
 * Ajustes de impresión POSKN (localStorage + modal).
 * Impresión silenciosa requiere el agente local (print-agent).
 */

const LS_KEY = 'poskn_print_settings_v1';

export const POSKN_PRINT_DEFAULTS = {
	method: 'browser', // browser | agent
	agentUrl: 'http://127.0.0.1:9199',
	connection: 'system', // system | network | bluetooth
	printerName: '',
	printerIp: '',
	printerPort: 9100,
	paperWidth: '80', // 58 | 80
	autoTicket: false,
	autoCierre: false,
	autoComanda: false,
	autoReporte: false,
	agentToken: '',
};

export function agentRequestHeaders(cfg) {
	const headers = {};
	const token = String((cfg && cfg.agentToken) || '').trim();
	if (token) headers['X-POSKN-Print-Token'] = token;
	return headers;
}

export function loadPrintSettings() {
	try {
		const raw = localStorage.getItem(LS_KEY);
		const base = raw ? { ...POSKN_PRINT_DEFAULTS, ...JSON.parse(raw) } : { ...POSKN_PRINT_DEFAULTS };
		if (typeof window !== 'undefined' && (window.POSKN_IS_DESKTOP || (window.posknDesktop && window.posknDesktop.isDesktop))) {
			base.method = 'agent';
			if (!base.agentUrl) base.agentUrl = POSKN_PRINT_DEFAULTS.agentUrl;
		}
		return base;
	} catch (e) {
		const fallback = { ...POSKN_PRINT_DEFAULTS };
		if (typeof window !== 'undefined' && (window.POSKN_IS_DESKTOP || (window.posknDesktop && window.posknDesktop.isDesktop))) {
			fallback.method = 'agent';
		}
		return fallback;
	}
}

export function savePrintSettings(partial) {
	const next = { ...loadPrintSettings(), ...partial };
	try {
		localStorage.setItem(LS_KEY, JSON.stringify(next));
	} catch (e) {
		/* ignore */
	}
	return next;
}

function normPrinter(name) {
	return String(name || '')
		.toLowerCase()
		.replace(/[\s_\-]+/g, '');
}

/** Virtual / no-térmicas: no usarlas como fallback en ningún local. */
export function isIgnoredPrinterName(name) {
	return /onenote|print to pdf|xps document|fax|anydesk|microsoft print|onenote/.test(
		String(name || '').toLowerCase(),
	);
}

/**
 * Heurística genérica (cualquier marca/nombre). No asume POS-58 ni un modelo.
 */
export function isThermalPrinterName(name) {
	const n = String(name || '').toLowerCase();
	if (!n || isIgnoredPrinterName(n)) return false;
	return /pos|thermal|t[eé]rmic|ticket|receipt|escpos|epson|star |citizen|bixolon|xprinter|xp-?\d|rongta|gprinter|generic|text only|tm-t|tsp/.test(
		n,
	);
}

/** Solo si el nombre deja claro 58 u 80 mm; si no, vacío (se respeta el ajuste del local). */
export function inferPaperWidthFromPrinter(name) {
	const n = String(name || '');
	if (/(^|[^0-9])58([^0-9]|$)/.test(n)) return '58';
	if (/(^|[^0-9])80([^0-9]|$)/.test(n)) return '80';
	return '';
}

export function normalizePaperWidth(value, fallback) {
	let w = String(value || '').trim();
	if (w === '2') w = '58';
	if (w === '3') w = '80';
	if (w === '58' || w === '80') return w;
	const fb = String(fallback || '').trim();
	if (fb === '58' || fb === '80') return fb;
	return '80';
}

/**
 * Resuelve impresora de ESTE equipo:
 * 1) nombre guardado en este PC si sigue existiendo
 * 2) mismo nombre con diferencias de espacios/guiones (POS58 v3 → POS-58)
 * 3) predeterminada de Windows si parece térmica
 * 4) primera térmica detectada
 * Nunca un modelo fijo.
 */
export function matchPrinterName(wanted, list, defaultPrinter) {
	const printers = Array.isArray(list) ? list : [];
	const w = String(wanted || '').trim();
	if (w && printers.includes(w)) return w;
	if (w) {
		const nw = normPrinter(w);
		const exact = printers.find((p) => normPrinter(p) === nw);
		if (exact) return exact;
		const part = printers.find((p) => {
			const np = normPrinter(p);
			return np.length >= 4 && (np.includes(nw) || nw.includes(np));
		});
		if (part) return part;
	}
	const def = String(defaultPrinter || '').trim();
	if (def && printers.includes(def) && isThermalPrinterName(def)) return def;
	const thermal = printers.find(isThermalPrinterName);
	return thermal || '';
}

export async function fetchAgentPrinters(agentUrl, cfg) {
	const url = String(agentUrl || POSKN_PRINT_DEFAULTS.agentUrl).replace(/\/$/, '');
	const res = await fetch(`${url}/printers`, { headers: agentRequestHeaders(cfg || loadPrintSettings()) });
	const data = await res.json().catch(() => ({}));
	return {
		printers: (data && data.printers) || [],
		defaultPrinter: (data && (data.default || data.defaultPrinter)) || '',
	};
}

export async function resolvePrintTarget(cfg) {
	const base = cfg || loadPrintSettings();
	let printers = [];
	let defaultPrinter = '';
	try {
		const info = await fetchAgentPrinters(base.agentUrl, base);
		printers = info.printers;
		defaultPrinter = info.defaultPrinter;
	} catch (e) {
		printers = [];
	}
	const printerName = matchPrinterName(base.printerName, printers, defaultPrinter);
	const remapped = !!(printerName && printerName !== String(base.printerName || '').trim());
	const inferred = inferPaperWidthFromPrinter(printerName);
	const savedPaper = normalizePaperWidth(base.paperWidth, '');
	const paperWidth = remapped && inferred ? inferred : savedPaper || inferred || '80';
	return {
		printerName,
		paperWidth,
		inferredPaper: inferred,
		remapped,
		printers,
		defaultPrinter,
	};
}

export function persistPaperWidth(paperWidth) {
	const w = paperWidth === '58' ? '58' : paperWidth === '80' ? '80' : '';
	if (!w) return;
	try {
		localStorage.setItem('poskn_ancho_impresora', w);
		localStorage.setItem('pos_ticket_formato', w);
	} catch (e) {
		/* ignore */
	}
}

function ensureModal($) {
	if ($('#modal-print-settings').length) return;

	const html = `
	<div class="modal-overlay" id="modal-print-settings" style="display:none;z-index:10050;">
		<div class="modal-content" style="max-width:480px;width:94%;">
			<div class="modal-header">
				<div class="modal-title">🖨️ Ajustes de impresión</div>
				<button type="button" class="btn-cerrar" id="poskn-print-settings-close">&times;</button>
			</div>
			<div style="padding:14px 16px;font-size:13px;max-height:70vh;overflow:auto;">
				<p style="margin:0 0 12px;color:#555;font-size:12px;line-height:1.4;">
					El navegador <strong>no puede</strong> imprimir sin diálogo a USB/BT/IP.
					Para impresión automática usa el <strong>agente local POSKN</strong> en esta PC
					(<code>print-agent</code>). Cada equipo guarda su propia impresora y ancho.
				</p>

				<label style="display:block;font-weight:700;margin-bottom:4px;">Modo de impresión</label>
				<select id="poskn-print-method" class="form-input" style="width:100%;margin-bottom:12px;">
					<option value="browser">Navegador (con diálogo)</option>
					<option value="agent">Agente local (sin diálogo)</option>
				</select>

				<div id="poskn-print-agent-block">
					<label style="display:block;font-weight:700;margin-bottom:4px;">URL del agente</label>
					<input type="text" id="poskn-print-agent-url" class="form-input" style="width:100%;margin-bottom:8px;" placeholder="http://127.0.0.1:9199">
					<label style="display:block;font-weight:700;margin-bottom:4px;">Token del agente (opcional)</label>
					<input type="password" id="poskn-print-agent-token" class="form-input" style="width:100%;margin-bottom:8px;" placeholder="Solo si token.txt o POSKN_PRINT_TOKEN" autocomplete="off">

					<label style="display:block;font-weight:700;margin-bottom:4px;">Conexión impresora</label>
					<select id="poskn-print-connection" class="form-input" style="width:100%;margin-bottom:8px;">
						<option value="system">Sistema (USB / Windows / BT instalada)</option>
						<option value="network">Red IP (ESC/POS puerto 9100)</option>
					</select>

					<div id="poskn-print-system-wrap">
						<label style="display:block;font-weight:700;margin-bottom:4px;">Impresora del sistema</label>
						<div style="display:flex;gap:6px;margin-bottom:8px;">
							<select id="poskn-print-printer-name" class="form-input" style="flex:1;">
								<option value="">-- Predeterminada del sistema --</option>
							</select>
							<button type="button" id="poskn-print-refresh-printers" class="form-input" style="width:auto;padding:8px 10px;background:#607D8B;color:#fff;border:none;cursor:pointer;">↻</button>
						</div>
					</div>

					<div id="poskn-print-network-wrap" style="display:none;">
						<label style="display:block;font-weight:700;margin-bottom:4px;">IP impresora</label>
						<input type="text" id="poskn-print-ip" class="form-input" style="width:100%;margin-bottom:8px;" placeholder="192.168.1.50">
						<label style="display:block;font-weight:700;margin-bottom:4px;">Puerto</label>
						<input type="number" id="poskn-print-port" class="form-input" style="width:100%;margin-bottom:8px;" value="9100">
					</div>

					<div style="display:flex;gap:8px;margin-bottom:12px;align-items:center;">
						<button type="button" id="poskn-print-test-agent" class="form-input" style="flex:1;background:#455A64;color:#fff;border:none;cursor:pointer;padding:10px;">Probar agente</button>
						<span id="poskn-print-agent-status" style="font-size:11px;color:#666;"></span>
					</div>
				</div>

				<label style="display:block;font-weight:700;margin-bottom:4px;">Ancho ticket</label>
				<select id="poskn-print-paper" class="form-input" style="width:100%;margin-bottom:12px;">
					<option value="58">58 mm</option>
					<option value="80">80 mm</option>
				</select>

				<div style="border-top:1px solid #eee;padding-top:10px;margin-bottom:12px;">
					<strong style="display:block;margin-bottom:8px;">Impresión automática</strong>
					<label style="display:flex;align-items:center;gap:8px;margin-bottom:6px;cursor:pointer;">
						<input type="checkbox" id="poskn-print-auto-ticket"> Tickets de venta al crear pedido
					</label>
					<label style="display:flex;align-items:center;gap:8px;margin-bottom:6px;cursor:pointer;">
						<input type="checkbox" id="poskn-print-auto-cierre"> Cierre / ticket de caja
					</label>
					<label style="display:flex;align-items:center;gap:8px;margin-bottom:6px;cursor:pointer;">
						<input type="checkbox" id="poskn-print-auto-comanda"> Comandas de cocina
					</label>
					<label style="display:flex;align-items:center;gap:8px;cursor:pointer;">
						<input type="checkbox" id="poskn-print-auto-reporte"> Reporte de caja abierta
					</label>
				</div>

				<button type="button" id="poskn-print-settings-save" class="form-input" style="width:100%;background:#4CAF50;color:#fff;border:none;cursor:pointer;padding:12px;font-weight:700;">
					Guardar ajustes
				</button>

				<div style="border-top:1px solid #eee;margin-top:14px;padding-top:12px;" id="poskn-reparar-pwa-wrap">
					<p style="margin:0 0 8px;color:#666;font-size:12px;line-height:1.35;">
						Si la app instalada (PWA) deja de abrir o se queda en versión vieja, repara la caché. En la app Windows usa el menú POSKN → Limpiar caché.
					</p>
					<button type="button" id="poskn-reparar-pwa" class="form-input" style="width:100%;background:#ff9800;color:#fff;border:none;cursor:pointer;padding:12px;font-weight:700;">
						Reparar app (limpiar caché PWA)
					</button>
				</div>
			</div>
		</div>
	</div>`;
	$('body').append(html);
}

function fillForm($, cfg) {
	$('#poskn-print-method').val(cfg.method || 'browser');
	$('#poskn-print-agent-url').val(cfg.agentUrl || POSKN_PRINT_DEFAULTS.agentUrl);
	$('#poskn-print-agent-token').val(cfg.agentToken || '');
	$('#poskn-print-connection').val(cfg.connection || 'system');
	$('#poskn-print-ip').val(cfg.printerIp || '');
	$('#poskn-print-port').val(cfg.printerPort || 9100);
	$('#poskn-print-paper').val(cfg.paperWidth || '80');
	$('#poskn-print-auto-ticket').prop('checked', !!cfg.autoTicket);
	$('#poskn-print-auto-cierre').prop('checked', !!cfg.autoCierre);
	$('#poskn-print-auto-comanda').prop('checked', !!cfg.autoComanda);
	$('#poskn-print-auto-reporte').prop('checked', !!cfg.autoReporte);

	const $sel = $('#poskn-print-printer-name');
	const current = cfg.printerName || '';
	if (current && !$sel.find('option').filter(function () { return $(this).val() === current; }).length) {
		$sel.append($('<option></option>').attr('value', current).text(current));
	}
	$sel.val(current);

	toggleBlocks($);
}

function toggleBlocks($) {
	const method = $('#poskn-print-method').val();
	const conn = $('#poskn-print-connection').val();
	$('#poskn-print-agent-block').toggle(method === 'agent');
	$('#poskn-print-system-wrap').toggle(conn === 'system');
	$('#poskn-print-network-wrap').toggle(conn === 'network');
}

function readForm($) {
	return {
		method: $('#poskn-print-method').val() || 'browser',
		agentUrl: ($('#poskn-print-agent-url').val() || '').trim() || POSKN_PRINT_DEFAULTS.agentUrl,
		agentToken: ($('#poskn-print-agent-token').val() || '').trim(),
		connection: $('#poskn-print-connection').val() || 'system',
		printerName: $('#poskn-print-printer-name').val() || '',
		printerIp: ($('#poskn-print-ip').val() || '').trim(),
		printerPort: parseInt($('#poskn-print-port').val(), 10) || 9100,
		paperWidth: $('#poskn-print-paper').val() || '80',
		autoTicket: $('#poskn-print-auto-ticket').is(':checked'),
		autoCierre: $('#poskn-print-auto-cierre').is(':checked'),
		autoComanda: $('#poskn-print-auto-comanda').is(':checked'),
		autoReporte: $('#poskn-print-auto-reporte').is(':checked'),
	};
}

async function refreshPrinters(win, $) {
	const cfg = readForm($);
	const url = (cfg.agentUrl || '').replace(/\/$/, '');
	$('#poskn-print-agent-status').text('Consultando...');
	try {
		const res = await fetch(`${url}/printers`, { method: 'GET', headers: agentRequestHeaders(cfg) });
		const data = await res.json();
		const list = (data && data.printers) || [];
		const $sel = $('#poskn-print-printer-name');
		const prev = $sel.val();
		$sel.html('<option value="">-- Predeterminada del sistema --</option>');
		list.forEach((name) => {
			$sel.append(`<option value="${String(name).replace(/"/g, '&quot;')}">${name}</option>`);
		});
		const pick = matchPrinterName(prev, list, data.default || data.defaultPrinter || '');
		if (pick) $sel.val(pick);
		$('#poskn-print-agent-status').css('color', '#2e7d32').text(`OK · ${list.length} impresora(s)`);
	} catch (e) {
		$('#poskn-print-agent-status').css('color', '#c62828').text('Agente no responde');
		if (typeof win.mostrarNotificacion === 'function') {
			win.mostrarNotificacion('No se pudo conectar al agente. ¿Está ejecutándose?', 'error');
		}
	}
}

async function testAgent(win, $) {
	const cfg = readForm($);
	const url = (cfg.agentUrl || '').replace(/\/$/, '');
	$('#poskn-print-agent-status').text('Probando...');
	try {
		const res = await fetch(`${url}/status`, { method: 'GET', headers: agentRequestHeaders(cfg) });
		const data = await res.json();
		if (data && data.ok) {
			$('#poskn-print-agent-status').css('color', '#2e7d32').text(`Online v${data.version || '?'}`);
			if (typeof win.mostrarNotificacion === 'function') {
				win.mostrarNotificacion('Agente de impresión conectado', 'success');
			}
			await refreshPrinters(win, $);
		} else {
			throw new Error('bad status');
		}
	} catch (e) {
		$('#poskn-print-agent-status').css('color', '#c62828').text('Sin conexión');
		if (typeof win.mostrarNotificacion === 'function') {
			win.mostrarNotificacion('Agente offline. Ejecuta print-agent en esta PC.', 'error');
		}
	}
}

export function attachPrintSettingsGlobals(win, $) {
	if (win._posknPrintSettingsBound) return;
	win._posknPrintSettingsBound = true;

	win.posknGetPrintSettings = loadPrintSettings;
	win.posknSavePrintSettings = savePrintSettings;

	win.abrirAjustesImpresion = function abrirAjustesImpresion() {
		ensureModal($);
		const isDesktop = !!(win.POSKN_IS_DESKTOP || (win.posknDesktop && win.posknDesktop.isDesktop));
		if (isDesktop) {
			$('#poskn-reparar-pwa-wrap').hide();
			const hint = $('#poskn-print-agent-block').prevAll('p').first();
			if (hint.length) {
				hint.html(
					'Estás en <strong>POSKN Desktop</strong>. Usa el <strong>agente local</strong> (sin diálogo). El agente se arranca con la app.',
				);
			}
		}
		fillForm($, loadPrintSettings());
		$('#modal-print-settings').css('display', 'flex');
		if (loadPrintSettings().method === 'agent') {
			testAgent(win, $);
		}
	};

	$(document).on('click', '#btn-print-settings, .btn-print-settings', (e) => {
		e.preventDefault();
		win.abrirAjustesImpresion();
	});

	$(document).on('click', '#poskn-print-settings-close', () => {
		$('#modal-print-settings').hide();
	});

	$(document).on('change', '#poskn-print-method, #poskn-print-connection', () => toggleBlocks($));

	$(document).on('click', '#poskn-print-refresh-printers', () => refreshPrinters(win, $));
	$(document).on('click', '#poskn-print-test-agent', () => testAgent(win, $));

	$(document).on('click', '#poskn-print-settings-save', () => {
		const cfg = savePrintSettings(readForm($));
		persistPaperWidth(cfg.paperWidth);
		try {
			$('#ancho-impresora-ticket, #ancho-impresora-comanda').val(cfg.paperWidth);
		} catch (e) {
			/* ignore */
		}
		$('#modal-print-settings').hide();
		if (typeof win.mostrarNotificacion === 'function') {
			win.mostrarNotificacion('Ajustes de impresión guardados', 'success');
		}
	});

	$(document).on('click', '#poskn-reparar-pwa', (e) => {
		e.preventDefault();
		if (typeof win.posknRepararAppPwa === 'function') {
			win.posknRepararAppPwa();
		} else {
			win.alert('Función de reparación no disponible. Recarga la página e intenta de nuevo.');
		}
	});
}
